import * as THREE from 'three';
import { DEG2RAD, rotateVec, type Quat, type Vec3 } from '../../sim/mathx';
import { SCOUT_CAMERA_CONFIG, scoutCameraPose } from '../../sim/vision';

export type CameraMode = 'fpv' | 'chase' | 'orbit' | 'radar-eye';

export interface CameraRigOptions {
  aspect?: number;
  fpvFovDeg?: number;
  viewFovDeg?: number;
  near?: number;
  far?: number;
  orbitFocusEnu?: Vec3 | null;
}

export interface OrbitSettings {
  yaw: number;
  pitch: number;
  distance: number;
}

export interface CameraRigExtras {
  scoutVelEnu?: Vec3;
  selectedTrackEnu?: Vec3 | null;
}

export const FPV_MOUNT_OFFSET: Readonly<Vec3> = Object.freeze({ x: 0.22, y: 0, z: 0.08 });
export const CHASE_OFFSET: Readonly<Vec3> = Object.freeze({ x: -6, y: 0, z: 2.5 });

/**
 * Reorients a Three camera into the simulation camera frame: local -Z becomes
 * FLU +X (forward), local +Y becomes FLU +Z (up), and local +X becomes FLU -Y.
 */
export const FLU_TO_THREE_CAMERA = new THREE.Quaternion(0.5, -0.5, -0.5, 0.5);

const DEFAULT_FPV_FOV_DEG = 120;
const DEFAULT_VIEW_FOV_DEG = 60;
const CHASE_ANGULAR_FREQUENCY = 6;
const MAX_SPRING_STEP_SECONDS = 0.1;
const MAX_ORBIT_PITCH = Math.PI / 2 - 1e-3;

const setPosition = (target: THREE.Vector3, source: Vec3): void => {
  target.set(source.x, source.y, source.z);
};

const setQuaternion = (target: THREE.Quaternion, source: Quat): void => {
  target.set(source.x, source.y, source.z, source.w).normalize();
};

const finiteDt = (dtSeconds: number): number =>
  Number.isFinite(dtSeconds) && dtSeconds > 0 ? Math.min(dtSeconds, MAX_SPRING_STEP_SECONDS) : 0;

/** Cameras driven only by an interpolated scout pose and explicitly supplied extras. */
export class CameraRig {
  mode: CameraMode = 'fpv';

  private readonly cameras: Record<CameraMode, THREE.PerspectiveCamera>;
  private readonly radarEnu: THREE.Vector3;
  private readonly chaseVelocity = new THREE.Vector3();
  private readonly desiredChasePosition = new THREE.Vector3();
  private readonly targetVelocity = new THREE.Vector3();
  private readonly orbit: OrbitSettings = { yaw: 0, pitch: 25 * DEG2RAD, distance: 25 };
  private orbitFocusEnu: THREE.Vector3 | null;

  constructor(radarEnu: Vec3 = { x: 0, y: 0, z: 0 }, options: CameraRigOptions = {}) {
    const aspect = options.aspect ?? SCOUT_CAMERA_CONFIG.aspect;
    const near = options.near ?? SCOUT_CAMERA_CONFIG.near;
    const far = options.far ?? SCOUT_CAMERA_CONFIG.far;
    const makeCamera = (fov: number): THREE.PerspectiveCamera => {
      const camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
      camera.up.set(0, 0, 1);
      return camera;
    };

    this.cameras = {
      fpv: makeCamera(options.fpvFovDeg ?? DEFAULT_FPV_FOV_DEG),
      chase: makeCamera(options.viewFovDeg ?? DEFAULT_VIEW_FOV_DEG),
      orbit: makeCamera(options.viewFovDeg ?? DEFAULT_VIEW_FOV_DEG),
      'radar-eye': makeCamera(options.viewFovDeg ?? DEFAULT_VIEW_FOV_DEG),
    };
    this.radarEnu = new THREE.Vector3(radarEnu.x, radarEnu.y, radarEnu.z);
    this.orbitFocusEnu = options.orbitFocusEnu
      ? new THREE.Vector3(options.orbitFocusEnu.x, options.orbitFocusEnu.y, options.orbitFocusEnu.z)
      : null;
  }

  activeCamera(): THREE.PerspectiveCamera {
    return this.cameras[this.mode];
  }

  setMode(mode: CameraMode): void {
    if (mode === this.mode) return;
    const previous = this.activeCamera();
    previous.updateMatrixWorld(true);
    this.mode = mode;
    if (mode === 'chase') {
      this.cameras.chase.position.copy(previous.getWorldPosition(new THREE.Vector3()));
      this.chaseVelocity.set(0, 0, 0);
      this.cameras.chase.updateMatrixWorld(true);
    }
  }

  setFov(deg: number): void {
    if (!Number.isFinite(deg) || deg <= 0 || deg >= 180) return;
    const camera = this.activeCamera();
    camera.fov = deg;
    camera.updateProjectionMatrix();
  }

  setOrbit(settings: OrbitSettings): void {
    if (Number.isFinite(settings.yaw)) this.orbit.yaw = settings.yaw;
    if (Number.isFinite(settings.pitch)) {
      this.orbit.pitch = THREE.MathUtils.clamp(settings.pitch, -MAX_ORBIT_PITCH, MAX_ORBIT_PITCH);
    }
    if (Number.isFinite(settings.distance) && settings.distance > 0) {
      this.orbit.distance = settings.distance;
    }
  }

  /** Pass null to resume orbiting around the scout. */
  setOrbitFocus(focusEnu: Vec3 | null): void {
    this.orbitFocusEnu = focusEnu ? new THREE.Vector3(focusEnu.x, focusEnu.y, focusEnu.z) : null;
  }

  update(
    interp: { scout: { posEnu: Vec3; attitude: Quat } },
    extras: CameraRigExtras,
    dtSeconds: number,
  ): void {
    switch (this.mode) {
      case 'fpv':
        this.updateFpv(interp.scout);
        break;
      case 'chase':
        this.updateChase(interp.scout, extras.scoutVelEnu, dtSeconds);
        break;
      case 'orbit':
        this.updateOrbit(interp.scout.posEnu);
        break;
      case 'radar-eye':
        this.updateRadarEye(extras.selectedTrackEnu ?? interp.scout.posEnu);
        break;
    }
    this.activeCamera().updateMatrixWorld(true);
  }

  private updateFpv(scout: { posEnu: Vec3; attitude: Quat }): void {
    const camera = this.cameras.fpv;
    const mountOffset = rotateVec(scout.attitude, FPV_MOUNT_OFFSET);
    camera.position.set(
      scout.posEnu.x + mountOffset.x,
      scout.posEnu.y + mountOffset.y,
      scout.posEnu.z + mountOffset.z,
    );

    const cameraPose = scoutCameraPose(scout, SCOUT_CAMERA_CONFIG.tiltDeg);
    setQuaternion(camera.quaternion, cameraPose.orientation);
    camera.quaternion.multiply(FLU_TO_THREE_CAMERA).normalize();
  }

  private updateChase(
    scout: { posEnu: Vec3; attitude: Quat },
    scoutVelocity: Vec3 | undefined,
    dtSeconds: number,
  ): void {
    const camera = this.cameras.chase;
    const rotatedOffset = rotateVec(scout.attitude, CHASE_OFFSET);
    this.desiredChasePosition.set(
      scout.posEnu.x + rotatedOffset.x,
      scout.posEnu.y + rotatedOffset.y,
      scout.posEnu.z + rotatedOffset.z,
    );
    if (scoutVelocity) setPosition(this.targetVelocity, scoutVelocity);
    else this.targetVelocity.set(0, 0, 0);

    const dt = finiteDt(dtSeconds);
    if (dt > 0) {
      this.stepCriticalSpring(camera.position, this.desiredChasePosition, this.targetVelocity, dt);
    }
    camera.lookAt(scout.posEnu.x, scout.posEnu.y, scout.posEnu.z);
  }

  private stepCriticalSpring(
    position: THREE.Vector3,
    target: THREE.Vector3,
    targetVelocity: THREE.Vector3,
    dt: number,
  ): void {
    const decay = Math.exp(-CHASE_ANGULAR_FREQUENCY * dt);
    for (const axis of ['x', 'y', 'z'] as const) {
      const targetAtStepStart = target[axis] - targetVelocity[axis] * dt;
      const error = position[axis] - targetAtStepStart;
      const relativeVelocity = this.chaseVelocity[axis] - targetVelocity[axis];
      const impulse = relativeVelocity + CHASE_ANGULAR_FREQUENCY * error;
      position[axis] = target[axis] + (error + impulse * dt) * decay;
      this.chaseVelocity[axis] =
        targetVelocity[axis] + (relativeVelocity - CHASE_ANGULAR_FREQUENCY * impulse * dt) * decay;
    }
  }

  private updateOrbit(scoutPosition: Vec3): void {
    const camera = this.cameras.orbit;
    const focus =
      this.orbitFocusEnu ?? new THREE.Vector3(scoutPosition.x, scoutPosition.y, scoutPosition.z);
    const horizontalDistance = this.orbit.distance * Math.cos(this.orbit.pitch);
    camera.position.set(
      focus.x + horizontalDistance * Math.cos(this.orbit.yaw),
      focus.y + horizontalDistance * Math.sin(this.orbit.yaw),
      focus.z + this.orbit.distance * Math.sin(this.orbit.pitch),
    );
    camera.lookAt(focus);
  }

  private updateRadarEye(target: Vec3): void {
    const camera = this.cameras['radar-eye'];
    camera.position.copy(this.radarEnu);
    camera.lookAt(target.x, target.y, target.z);
  }
}
